import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { normalizeSolutionMath } from "../../lib/solutionMath";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ADMIN } from "../../constants/testIds";
import { Trash, AlertTriangle } from "lucide-react";
import BlockEditor, { ReplaceImageBtn } from "../../components/BlockEditor";
import { QuestionContent, ChoiceContent } from "../../components/QuestionContent";
import { RichMath } from "../../components/KaTeX";
import Autocomplete from "../../components/Autocomplete";
import BulkImportModal from "../../components/BulkImportModal";

const EXAM_LEVELS = ["LGS", "TYT", "AYT"];

// Türkçe uyumlu slug: küçük harf, boşluk → tire, aksan/özel karakter atılır.
const _slug = (s) => (s || "")
  .replace(/[çÇ]/g, "c").replace(/[ğĞ]/g, "g").replace(/[ıİI]/g, "i")
  .replace(/[öÖ]/g, "o").replace(/[şŞ]/g, "s").replace(/[üÜ]/g, "u")
  .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const EMPTY = {
  exam_level: "",
  text: "", latex: "", image_path: null,
  blocks: [],
  choices: [
    { key: "A", text: "", image_path: null },
    { key: "B", text: "", image_path: null },
    { key: "C", text: "", image_path: null },
    { key: "D", text: "", image_path: null },
    { key: "E", text: "", image_path: null },
  ],
  correct: "A", topic_id: "", subtopic_id: "", tags: [],
  difficulty: "orta", source_type: "kendi", source_info: "",
  explanation: "", solution: "", youtube_url: "", is_draft: false, is_active: true,
};

export default function AdminQuestionEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagQuery, setTagQuery] = useState("");
  const [similar, setSimilar] = useState([]);
  const [savedRef, setSavedRef] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  useEffect(() => {
    (async () => {
      const [t, s, g] = await Promise.all([
        api.get("/catalog/topics"), api.get("/catalog/subtopics"), api.get("/catalog/tags"),
      ]);
      setTopics(t.data); setSubtopics(s.data); setTags(g.data);
      if (id && id !== "yeni") {
        const { data } = await api.get(`/questions/${id}`);
        const choices = ["A", "B", "C", "D", "E"].map((k) => {
          const found = (data.choices || []).find((c) => c.key === k);
          return found ? { image_path: null, ...found } : { key: k, text: "", image_path: null };
        });
        setForm({ ...EMPTY, ...data, blocks: data.blocks || [], choices, exam_level: data.exam_level || "" });
        setSavedRef(data.ref);
      }
    })();
  }, [id]);

  // Topics filtered by selected exam_level (isolation).
  const topicOptions = useMemo(() =>
    topics
      .filter((t) => (form.exam_level ? t.exam_level === form.exam_level : true))
      .map((t) => ({ id: t.topic_id, name: t.name, meta: t.slug })),
    [topics, form.exam_level]
  );
  const subtopicOptions = useMemo(() =>
    subtopics
      .filter((s) => s.topic_id === form.topic_id)
      .map((s) => ({ id: s.subtopic_id, name: s.name, meta: s.slug })),
    [subtopics, form.topic_id]
  );
  const tagOptions = useMemo(() => {
    const chosen = new Set(form.tags.map((x) => x.tag_id));
    const q = tagQuery.trim().toLowerCase();
    return tags
      .filter((t) => !chosen.has(t.tag_id))
      .filter((t) => !q || (t.name || "").toLowerCase().includes(q))
      .slice(0, 20);
  }, [tags, tagQuery, form.tags]);

  const setChoice = (k, patch) =>
    setForm((f) => ({ ...f, choices: f.choices.map((c) => c.key === k ? { ...c, ...patch } : c) }));

  const setTagImpact = (tag_id, impact) =>
    setForm((f) => ({ ...f, tags: f.tags.map((t) => t.tag_id === tag_id ? { ...t, impact } : t) }));

  const addTag = (tag_id) => {
    setForm((f) => ({ ...f, tags: [...f.tags, { tag_id, impact: "medium" }] }));
    setTagQuery("");
  };
  const removeTag = (tag_id) =>
    setForm((f) => ({ ...f, tags: f.tags.filter((t) => t.tag_id !== tag_id) }));

  const hasContent = (form.blocks && form.blocks.length > 0) || !!(form.text || "").trim();

  const save = async () => {
    if (!form.exam_level) { toast.error("Sınav seviyesi seçilmelidir (LGS/TYT/AYT)"); return; }
    if (!hasContent) { toast.error("En az bir içerik bloğu veya metin gerekli"); return; }
    if (!form.topic_id) { toast.error("Konu seçilmelidir"); return; }
    const legacyText = form.text || (form.blocks || []).find((b) => b.type === "text")?.value || "";
    const payload = {
      ...form, text: legacyText,
      explanation: normalizeSolutionMath(form.explanation || ""),
      solution: normalizeSolutionMath(form.solution || ""),
    };
    setBusy(true);
    try {
      if (id && id !== "yeni") {
        const { data } = await api.put(`/questions/${id}`, payload);
        const flipped = data?.answer_change_reevaluation?.flipped;
        toast.success(flipped ? `Güncellendi — ${flipped} geçmiş yanıt yeniden değerlendirildi` : "Güncellendi");
        setSavedRef(data.ref);
      } else {
        const { data } = await api.post("/questions", payload);
        setSavedRef(data.question.ref);
        setSimilar(data.similar || []);
        toast.success(`Kaydedildi: ${data.question.ref}`);
        nav(`/admin/sorular/${data.question.question_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl tracking-tight">
            {id && id !== "yeni" ? "Soru Düzenle" : "Yeni Soru"}
          </h1>
          {savedRef && <div className="font-mono text-sm text-[#EA580C]">{savedRef}</div>}
        </div>
        <div className="flex items-center gap-2">
          {(!id || id === "yeni") && (
            <button onClick={() => setShowBulk(true)} data-testid="admin-editor-bulk-import-btn"
              className="px-3 py-2 rounded-lg bg-[#171717] border border-[#262626] hover:border-[#EA580C] text-sm">
              JSON Toplu Import
            </button>
          )}
          <button onClick={save} disabled={busy} data-testid={ADMIN.editorSaveBtn}
            className="px-4 py-2 rounded-lg bg-[#EA580C] hover:bg-[#F97316] disabled:opacity-60">
            {busy ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>

      <BulkImportModal open={showBulk} onClose={() => setShowBulk(false)} />

      {similar.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm flex gap-2">
          <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-amber-200">Bu soruya çok benzeyen mevcut sorular bulundu:</div>
            <ul className="mt-1 font-mono text-amber-100">
              {similar.map((s) => <li key={s.question_id}>{s.ref} · benzerlik {s.score}</li>)}
            </ul>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_420px] gap-4">
        <div className="space-y-5">
          <section className="rounded-lg border border-[#262626] bg-[#0F0F0F] p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-medium">Soru İçeriği</div>
                <div className="text-xs text-slate-400">
                  Bloklar sırasıyla öğrenciye gösterilir. İstediğin sırada Metin, LaTeX ve Görsel ekle.
                </div>
              </div>
            </div>
            <BlockEditor blocks={form.blocks || []} onChange={(next) => setForm({ ...form, blocks: next })} />
          </section>

          <section className="rounded-lg border border-[#262626] bg-[#0F0F0F] p-4">
            <div className="text-sm font-medium mb-3">Şıklar</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {form.choices.map((c) => (
                <div key={c.key} data-testid={`admin-choice-${c.key}`}
                  className={`rounded-md border p-3 ${form.correct === c.key ? "border-[#EA580C]" : "border-[#262626]"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-sm">
                      <span className={form.correct === c.key ? "text-[#EA580C]" : "text-slate-300"}>{c.key}</span>
                    </div>
                    <label className="text-xs text-slate-400 inline-flex items-center gap-1">
                      <input type="radio" name="correct" checked={form.correct === c.key}
                        onChange={() => setForm({ ...form, correct: c.key })}
                        data-testid={`admin-correct-${c.key}`} />
                      Doğru
                    </label>
                  </div>
                  <input data-testid={ADMIN.editorChoice(c.key)} value={c.text || ""}
                    onChange={(e) => setChoice(c.key, { text: e.target.value })}
                    placeholder="Şık metni ($…$ ile LaTeX)"
                    className="w-full px-2 py-1.5 rounded-md bg-[#0A0A0A] border border-[#262626] text-sm mb-2" />
                  <div className="flex items-center gap-2">
                    {c.image_path && (
                      <img alt={`sik-${c.key}`}
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/file?path=${encodeURIComponent(c.image_path)}`}
                        className="max-h-16 rounded border border-[#262626]" />
                    )}
                    <ReplaceImageBtn onPath={(p) => setChoice(c.key, { image_path: p })}
                      label={c.image_path ? "Görseli değiştir" : "Görsel ekle"}
                      testId={`admin-choice-image-${c.key}`} />
                    {c.image_path && (
                      <button onClick={() => setChoice(c.key, { image_path: null })}
                        className="text-xs text-rose-400">Kaldır</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="space-y-3">
          <section className="rounded-lg border border-[#262626] bg-[#171717] p-4">
            <div className="text-xs text-slate-400 mb-2 uppercase tracking-widest">Öğrenci Önizlemesi</div>
            <div className="text-white/95">
              <QuestionContent question={{ blocks: form.blocks, text: form.text, latex: form.latex, image_path: form.image_path }} />
            </div>
            <div className="mt-4 space-y-2">
              {form.choices.map((c) => (
                <div key={c.key} className={`text-sm flex items-start gap-2 rounded-md p-2 ${form.correct === c.key ? "bg-emerald-500/10 text-emerald-300" : "text-slate-300"}`}>
                  <span className="font-mono mt-0.5">{c.key})</span>
                  <ChoiceContent choice={c} />
                </div>
              ))}
            </div>
          </section>

          <Field label="Sınav Seviyesi (zorunlu)">
            <select
              data-testid="admin-editor-exam-level"
              value={form.exam_level || ""}
              onChange={(e) => setForm({ ...form, exam_level: e.target.value, topic_id: "", subtopic_id: "" })}
              className="w-full px-3 py-2 rounded-md bg-[#171717] border border-[#262626] focus:border-[#EA580C]">
              <option value="">Seçiniz…</option>
              {EXAM_LEVELS.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
            </select>
          </Field>

          <Field label="Konu (arama ile seç)">
            <Autocomplete
              value={form.topic_id || null}
              options={topicOptions}
              onChange={(id) => setForm({ ...form, topic_id: id || "", subtopic_id: "" })}
              onCreate={async (name) => {
                if (!form.exam_level) { toast.error("Önce sınav seviyesi seçin"); return null; }
                const slug = _slug(name) + "-" + form.exam_level.toLowerCase();
                try {
                  const { data } = await api.post("/catalog/topics", {
                    name, slug, exam_level: form.exam_level, is_free: false, is_active: true,
                    order: topics.length,
                  });
                  setTopics((prev) => [...prev, data]);
                  toast.success(`"${name}" konusu oluşturuldu`);
                  return { id: data.topic_id, name: data.name };
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Konu oluşturulamadı");
                  return null;
                }
              }}
              placeholder={form.exam_level ? "Örn. Mutlak Değer…" : "Önce sınav seviyesi seç"}
              disabled={!form.exam_level}
              testId="admin-editor-topic"
            />
          </Field>

          <Field label="Alt Konu (arama ile seç, opsiyonel)">
            <Autocomplete
              value={form.subtopic_id || null}
              options={subtopicOptions}
              onChange={(id) => setForm({ ...form, subtopic_id: id || "" })}
              onCreate={async (name) => {
                if (!form.topic_id) { toast.error("Önce konu seçin"); return null; }
                const slug = _slug(name);
                try {
                  const { data } = await api.post("/catalog/subtopics", {
                    topic_id: form.topic_id, name, slug,
                    exam_level: form.exam_level, order: subtopicOptions.length, is_active: true,
                  });
                  setSubtopics((prev) => [...prev, data]);
                  toast.success(`"${name}" alt konusu oluşturuldu`);
                  return { id: data.subtopic_id, name: data.name };
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Alt konu oluşturulamadı");
                  return null;
                }
              }}
              placeholder={form.topic_id ? "Örn. Mutlak Değerli Eşitsizlikler…" : "Önce konu seç"}
              disabled={!form.topic_id}
              allowEmpty
              testId="admin-editor-subtopic"
            />
          </Field>

          <Field label="Açıklama / Çözüm">
            <textarea data-testid={ADMIN.editorExplanation} rows={3} value={form.explanation || ""}
              onChange={(e) => setForm({ ...form, explanation: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-[#171717] border border-[#262626] font-mono text-sm" />
          </Field>

          <Field label="Çözüm (detaylı, LaTeX destekli)">
            <textarea data-testid="admin-editor-solution" rows={4} value={form.solution || ""}
              onChange={(e) => setForm({ ...form, solution: e.target.value })}
              placeholder="Adım adım çözüm… ($…$ ile LaTeX)"
              className="w-full px-3 py-2 rounded-md bg-[#171717] border border-[#262626] font-mono text-sm" />
            {(form.solution || "").trim() && (
              <div data-testid="admin-editor-solution-preview"
                className="mt-2 rounded-md border border-[#262626] bg-[#0A0A0A] p-3 text-sm text-slate-200">
                <div className="text-xs text-slate-500 mb-1">Önizleme</div>
                <RichMath text={normalizeSolutionMath(form.solution)} />
              </div>
            )}
          </Field>

          <section className="rounded-lg border border-[#262626] bg-[#0F0F0F] p-3">
            <div className="text-xs text-slate-400 mb-2">Etiketler (ara-seç) ve etki düzeyleri</div>

            <div className="relative">
              <input data-testid="admin-editor-tag-search"
                value={tagQuery} onChange={(e) => setTagQuery(e.target.value)}
                placeholder="Etiket ara…"
                className="w-full px-3 py-2 rounded-md bg-[#171717] border border-[#262626] focus:border-[#EA580C] outline-none text-sm" />
              {tagQuery.trim() && (
                <div className="mt-1 max-h-48 overflow-auto rounded-md bg-[#0A0A0A] border border-[#262626]">
                  {tagOptions.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-500">Uygun etiket bulunamadı</div>
                  ) : tagOptions.map((t) => (
                    <button key={t.tag_id} type="button"
                      data-testid={`admin-editor-tag-opt-${t.tag_id}`}
                      onClick={() => addTag(t.tag_id)}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-[#171717]">
                      {t.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {form.tags.length > 0 && (
              <div className="space-y-1 mt-3">
                {form.tags.map((tg) => {
                  const t = tags.find((x) => x.tag_id === tg.tag_id);
                  return (
                    <div key={tg.tag_id} data-testid={`admin-editor-tag-item-${tg.tag_id}`}
                      className="flex items-center gap-2 text-sm">
                      <span className="flex-1">{t?.name || "?"}</span>
                      <select
                        data-testid={`admin-editor-tag-importance-${tg.tag_id}`}
                        value={tg.impact} onChange={(e) => setTagImpact(tg.tag_id, e.target.value)}
                        className="px-2 py-1 rounded-md bg-[#171717] border border-[#262626] text-xs">
                        <option value="high">HIGH</option>
                        <option value="medium">MEDIUM</option>
                        <option value="low">LOW</option>
                      </select>
                      <button type="button" onClick={() => removeTag(tg.tag_id)} className="text-rose-400"
                        data-testid={`admin-editor-tag-remove-${tg.tag_id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      {children}
    </label>
  );
}
